/**
 * AdditionalDocuments - Aufklärungsdokumente für Online-Anamnese
 *
 * Zeigt Lokalanästhesie-Aufklärung und Ausfallhonorar-Vereinbarung
 * Dokument muss zu Ende gescrollt werden bevor man akzeptieren kann
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as AcceptIcon,
  KeyboardArrowDown as ScrollIcon
} from '@mui/icons-material';

const AdditionalDocuments = ({ type, patientName, onBack, onAccept }) => {
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const scrollRef = useRef(null);

  // Reset scroll state wenn Dokument-Typ wechselt
  useEffect(() => {
    setHasScrolledToEnd(false);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [type]);

  // Scroll-Handler - prüft ob zu Ende gescrollt
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Toleranz von 20px für "Ende erreicht"
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToEnd(true);
    }
  };

  // Prüfe initial ob Content kürzer als Container ist (kein Scroll nötig)
  useEffect(() => {
    const checkIfScrollNeeded = () => {
      if (scrollRef.current) {
        const { scrollHeight, clientHeight } = scrollRef.current;
        if (scrollHeight <= clientHeight + 20) {
          setHasScrolledToEnd(true);
        }
      }
    };
    // Kurze Verzögerung damit Content gerendert ist
    setTimeout(checkIfScrollNeeded, 100);
  }, [type]);

  // Dokument-Inhalte
  const documents = {
    lokalanaesthesie: {
      title: 'Lokalanästhesie',
      content: (
        <>
          <Typography variant="body1" paragraph>
            Die zahnärztliche Lokalanästhesie (örtliche Betäubung) dient der lokalen Ausschaltung der
            Schmerzempfindung im Zahn-, Mund-, Kiefer- und Gesichtsbereich. Durch sie können die notwendigen
            Behandlungen (z. B. Füllungen, Wurzelkanalbehandlungen, Extraktionen, Operationen) in der Regel
            schmerzfrei durchgeführt werden.
          </Typography>
          <Typography variant="body1" paragraph>
            Die Entscheidung über die Anwendung der Lokalanästhesie wird jeweils für die entsprechende
            Situation individuell getroffen und mit Ihnen besprochen. Überwiegend wird der betreffende
            Bereich vom Nervus trigeminus, einem Hirnnerv, mit Gefühl versorgt. Um das entsprechende Gebiet
            zu betäuben, wird die Lokalanästhesie möglichst dicht an kleinere Nervenfasern (Infiltrationsanästhesie)
            oder in die Nähe eines Hauptastes der Nerven (Leitungsanästhesie) platziert.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
            Mögliche Nebenwirkungen und Komplikationen
          </Typography>
          <Typography variant="body1" paragraph>
            Obwohl die Lokalanästhesie ein sicheres Verfahren zur Schmerzausschaltung ist, sind Nebenwirkungen
            und Unverträglichkeiten der verwendeten Substanz nicht unbedingt vermeidbar. Ferner kann es u. a.
            zu folgenden Komplikationen kommen:
          </Typography>

          <Box component="ul" sx={{ pl: 3, '& li': { mb: 2 } }}>
            <li>
              <Typography variant="body1">
                <strong>Hämatom (Bluterguss):</strong> Durch Verletzung kleiner Blutgefäße können Blutungen in das umgebende Gewebe eintreten. Bei
                Einblutungen in einen der Kaumuskeln infolge der Injektion kann es zu einer Behinderung der
                Mundöffnung und Schmerzen, in ganz seltenen Fällen auch Infektionen, kommen.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Nervschädigung:</strong> In sehr seltenen Fällen kann eine Irritation von Nervenfasern eintreten. Hierdurch sind
                vorübergehende bzw. dauerhafte Gefühlsstörungen möglich. Dies betrifft vor allem die Injektion
                im Unterkiefer, bei der die entsprechende Zungen- oder Unterkieferhälfte sowie die Lippenregion
                betroffen sein kann. Eine spezielle Therapie gibt es derzeit nicht. Die spontane Heilung muss
                abgewartet werden. <strong>Sollte das Taubheitsgefühl nicht nach maximal 12 Stunden vollständig
                abgeklungen sein, informieren Sie uns bitte.</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Verkehrstüchtigkeit:</strong> Infolge der örtlichen Betäubung und der zahnärztlichen Behandlung kann es zu einer
                Beeinträchtigung der Reaktions- und Konzentrationsfähigkeit kommen. Sie sollten daher
                während dieser Zeit nicht aktiv am Straßenverkehr teilnehmen.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Selbstverletzung:</strong> Sollten außer dem behandelten Zahn auch die umgebenden Weichteile (z. B. Zunge, Wangen, Lippen)
                betäubt sein, verzichten Sie bitte, solange dieser Zustand anhält, auf die Nahrungsaufnahme.
                Neben Bissverletzungen sind auch Verbrennungen und Erfrierungen möglich.
              </Typography>
            </li>
          </Box>

          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body1">
              <strong>Mit dem Klick auf "Gelesen & Akzeptiert" bestätige ich, dass ich über die
              zahnärztliche Lokalanästhesie aufgeklärt wurde und mit deren Anwendung einverstanden bin.</strong>
            </Typography>
          </Alert>
        </>
      )
    },
    ausfallhonorar: {
      title: 'Vereinbarung Ausfallhonorar',
      content: (
        <>
          <Typography variant="body1" paragraph>
            {patientName ? `Sehr geehrte/r ${patientName},` : 'Sehr geehrte Patientin, sehr geehrter Patient,'}
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>Die Zahnarztpraxis Dr. Hezel ist eine Terminpraxis.</strong> Das bedeutet: Die für Ihre Behandlung
            notwendige Zeit ist ausschließlich für Sie reserviert. Ihr Termin ist verbindlich - wir planen
            fest mit Ihrem Erscheinen und bereiten uns auf Ihre Behandlung vor.
          </Typography>

          <Typography variant="body1" paragraph>
            Aufgrund allgemein zunehmender Unzuverlässigkeit und kurzfristiger Absagen ist diese
            Vereinbarung Voraussetzung für die Terminvergabe.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
            Vereinbarung Ausfallhonorar
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mb: 3,
              bgcolor: '#fff3e0',
              borderColor: 'error.main',
              borderWidth: 2
            }}
          >
            <Typography variant="h6" sx={{ color: 'error.main', mb: 2 }}>
              Wenn Sie Ihren Termin unentschuldigt versäumen, wird ein Ausfallhonorar in Höhe von
              <strong> 80,00 Euro </strong> fällig.
            </Typography>
            <Typography variant="body1">
              Sie können Ihren Termin bis zu <strong>24 Stunden</strong> vorher per Telefon oder E-Mail absagen.
            </Typography>
          </Paper>

          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body1">
              <strong>Ich habe die Vereinbarung zum Ausfallhonorar verstanden und werde meine Termine
              zuverlässig wahrnehmen. Bei unentschuldigtem Versäumen ist mir bewusst, dass ein
              Ausfallhonorar von 80,00 Euro anfällt.</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Mit dem Klick auf "Gelesen & Akzeptiert" bestätige ich diese Vereinbarung.
            </Typography>
          </Alert>
        </>
      )
    }
  };

  const doc = documents[type];

  if (!doc) {
    return null;
  }

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
        {doc.title}
      </Typography>

      <Paper
        ref={scrollRef}
        variant="outlined"
        onScroll={handleScroll}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2,
          maxHeight: { xs: '45vh', sm: '55vh' },
          overflow: 'auto'
        }}
      >
        {doc.content}
      </Paper>

      {/* Scroll-Hinweis wenn noch nicht zu Ende gescrollt */}
      {!hasScrolledToEnd && (
        <Alert
          severity="info"
          icon={<ScrollIcon />}
          sx={{ mb: 2 }}
        >
          Bitte scrollen Sie das Dokument bis zum Ende, um fortzufahren.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={onBack}
          sx={{ flex: 1 }}
        >
          Zurück
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<AcceptIcon />}
          onClick={() => onAccept(type)}
          disabled={!hasScrolledToEnd}
          sx={{ flex: 1 }}
        >
          {hasScrolledToEnd ? 'Gelesen & Akzeptiert' : 'Bitte erst lesen...'}
        </Button>
      </Box>
    </Box>
  );
};

export default AdditionalDocuments;
